import React, { useState } from 'react';
import Layout from '../../components/Layout';
import { truckingReportExtAPI, truckingBillAPI } from '../../api/waybillAPI';

const PHP = (n) =>
  Number(n || 0).toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return `${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}/${dt.getFullYear()}`;
};

const today = () => new Date().toISOString().slice(0, 10);
const startOfMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

// Flat CSV — one row per waybill span line, mirroring RevenuePerVanPage.jsx's
// exportCSV convention (plain headers + plain data, no injected group rows).
// A CSV can't merge cells like the print/screen view does, so the per-span
// TOTAL AMOUNT PER SPAN value is written only on that span's first row and
// left blank on the rest — matching how the merged cell visually reads.
function exportCSV(groups, soaNumber) {
  const fmtD = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    return `${String(dt.getMonth() + 1).padStart(2, '0')}/${String(dt.getDate()).padStart(2, '0')}/${dt.getFullYear()}`;
  };
  const fmtNum = (n) => Number(n || 0).toFixed(2);

  const headers = ['BILLING #', 'SOA #', 'DATE', 'PLATE NO.', 'WB NO.', 'VESSEL / VOY. NO.', 'VAN NO.', 'CONSIGNEE', 'LOCATION', 'RATES', 'TOTAL AMOUNT PER SPAN', 'DATE RETURN'];
  const rows = [];
  groups.forEach((g) => {
    (g.spans || []).forEach((span) => {
      span.rows.forEach((r, i) => {
        rows.push([
          g.billName || '',
          g.soaNumber || '',
          fmtD(r.date),
          r.plateNo || '',
          r.wbNo || '',
          r.vesselVoyage || '',
          r.vanNo || '',
          r.consignee || '',
          r.location || '',
          fmtNum(r.total),
          i === 0 ? fmtNum(span.subtotal.total) : '',
          r.dateReturn ? fmtD(r.dateReturn) : '',
        ]);
      });
    });
  });
  const csv = [headers, ...rows]
    .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
    .join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `psacc-billing${soaNumber ? `-${soaNumber}` : ''}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function PSACCBillingReportPage() {
  const [dateFrom,   setDateFrom]   = useState(startOfMonth());
  const [dateTo,     setDateTo]     = useState(today());
  const [soaNumber,  setSoaNumber]  = useState('');
  const [groups,     setGroups]     = useState([]);
  const [summary,    setSummary]    = useState(null);
  const [storeInfo,  setStoreInfo]  = useState({});
  const [loading,    setLoading]    = useState(false);
  const [loaded,     setLoaded]     = useState(false);
  const [error,      setError]      = useState('');

  const hasRows = groups.some((g) => g.rows.length > 0);

  const handleDateReturnChange = async (billId, lineId, value) => {
    if (!lineId) return;
    try {
      await truckingBillAPI.updateLineDateReturn(billId, lineId, value);
      setGroups((prev) => prev.map((g) => {
        if (g.billId !== billId) return g;
        const applyToRows = (rows) => rows.map((r) => (r.lineId === lineId ? { ...r, dateReturn: value } : r));
        return {
          ...g,
          rows: applyToRows(g.rows),
          spans: (g.spans || []).map((span) => ({ ...span, rows: applyToRows(span.rows) })),
        };
      }));
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to update date return.');
    }
  };

  const load = async () => {
    setLoading(true); setError('');
    try {
      const { data } = await truckingReportExtAPI.getPSACCBilling({ dateFrom, dateTo, soaNumber });
      setGroups(data.groups || []);
      setSummary(data.summary || {});
      setStoreInfo(data.storeInfo || {});
      setLoaded(true);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  };

  // Print opens a dedicated, standalone print route (same pattern as
  // Revenue Per Van) instead of window.print() on this page — this page
  // is wrapped in Layout and uses card-style utility classes (rounded-lg,
  // shadow-md, bg-gray-50) that a global print stylesheet hides, which
  // was silently blanking the report when printed in place.
  const handlePrint = () => {
    const params = new URLSearchParams({ dateFrom, dateTo, ...(soaNumber ? { soaNumber } : {}) });
    window.open(`/billing/reports/print/psacc-billing?${params}`, '_blank');
  };

  return (
    <Layout pageTitle="PSACC Billing Report">
      {/* Filter Bar */}
      <div className="bg-white border rounded-lg p-4 mb-4 flex flex-wrap gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Date From</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="border rounded px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Date To</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="border rounded px-2 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">SOA Number</label>
          <input type="text" value={soaNumber} onChange={e => setSoaNumber(e.target.value)}
            placeholder="e.g. 2026-015"
            className="border rounded px-2 py-1.5 text-sm w-36" />
        </div>
        <button onClick={load} disabled={loading}
          className="px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700 disabled:opacity-50">
          {loading ? 'Loading…' : 'Load Report'}
        </button>
        {loaded && hasRows && (
          <>
            <button onClick={handlePrint}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200">
              Print
            </button>
            <button onClick={() => exportCSV(groups, soaNumber)}
              className="px-4 py-2 bg-green-50 text-green-700 border border-green-200 rounded-lg text-sm font-medium hover:bg-green-100">
              Export CSV
            </button>
          </>
        )}
      </div>

      {error && <p className="text-red-600 text-sm mb-3">{error}</p>}

      {loaded && (
        <div className="bg-white border rounded-lg p-4">
          {/* Company Header */}
          <div className="mb-4 text-center">
            <p className="font-bold text-base">{storeInfo.companyName || 'ARVANNAH RENZ TRUCKING INC.'}</p>
            <p className="text-sm">{storeInfo.address || 'Q. ABETO ST. BRGY. ABETO-MIRASOL TAFT SOUTH MANDURIAO, ILOILO CITY'}</p>
            <p className="text-sm">{storeInfo.tin ? `TIN NO. ${storeInfo.tin}` : 'TIN NO. 768-950-988-000-VAT'}</p>
            <p className="text-sm font-medium mt-1">
              STATEMENT OF ACCOUNT NO. {soaNumber || '___________'}
            </p>
          </div>

          {/* Addressed-to + Date */}
          <div className="flex justify-between mb-4 text-sm">
            <div>
              <p className="font-medium">PHILIPPINE SPAN ASIA CARRIER CORP.</p>
              <p>Coastal Road, Brgy. Balabago, Jaro, Iloilo City</p>
            </div>
            <div className="text-right">
              <p>Date: <span className="font-medium">{fmtDate(new Date())}</span></p>
            </div>
          </div>

          {/* Grouped table — Billing group -> Vessel/Voy span sub-group -> subtotals */}
          <div className="overflow-x-auto mb-4">
            <table className="min-w-full text-xs border-collapse">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border px-2 py-1">DATE</th>
                  <th className="border px-2 py-1">PLATE NO.</th>
                  <th className="border px-2 py-1">WB NO.</th>
                  <th className="border px-2 py-1">VESSEL / VOY. NO.</th>
                  <th className="border px-2 py-1">VAN NO.</th>
                  <th className="border px-2 py-1">CONSIGNEE</th>
                  <th className="border px-2 py-1">LOCATION</th>
                  <th className="border px-2 py-1 text-right">RATES</th>
                  <th className="border px-2 py-1 text-right">TOTAL AMOUNT PER SPAN</th>
                  <th className="border px-2 py-1">DATE RETURN</th>
                </tr>
              </thead>
              <tbody>
                {groups.length === 0 ? (
                  <tr><td colSpan={10} className="text-center py-6 text-gray-400 border">No billing records found for this range.</td></tr>
                ) : groups.map((g) => (
                  <React.Fragment key={g.billId}>
                    {/* Billing header row 
                    <tr className="bg-teal-50">
                      <td colSpan={10} className="border px-2 py-2">
                        <div className="text-right">
                          <p className="font-medium text-teal-900">STATEMENT OF ACCOUNT NO. {g.soaNumber || g.billName}</p>
                          <p className="text-teal-700">Date: {fmtDate(g.billDate)}</p>
                        </div>
                        <p className="text-[11px] text-teal-700 mt-1">
                          Billing: {g.billName} —
                          <span className={`ml-1 inline-block px-2 py-0.5 rounded-full text-[10px] font-medium ${
                            g.isVoided ? 'bg-red-100 text-red-600' : 'bg-teal-100 text-teal-700'
                          }`}>
                            {g.isVoided ? 'VOIDED' : (g.status || 'draft').toUpperCase()}
                          </span>
                          <span className="ml-2">({g.waybillCount} waybill{g.waybillCount === 1 ? '' : 's'})</span>
                        </p>
                      </td>
                    </tr>*/}

                    {(g.spans || []).length === 0 ? (
                      <tr>
                        <td colSpan={10} className="border px-2 py-1 text-center text-gray-400 italic">No waybills attached.</td>
                      </tr>
                    ) : g.spans.map((span, si) => (
                      <React.Fragment key={si}>
                        {/* Vessel/Voy span sub-header 
                        <tr style={{ backgroundColor: '#f0fdfa' }}>
                          <td colSpan={10} className="border px-2 py-1 font-medium text-teal-800">
                            VESSEL / VOY. NO.: {span.key}
                          </td>
                        </tr>*/}
                        {span.rows.map((r, i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="border px-2 py-1 whitespace-nowrap">{fmtDate(r.date)}</td>
                            <td className="border px-2 py-1">{r.plateNo}</td>
                            <td className="border px-2 py-1">{r.wbNo}</td>
                            <td className="border px-2 py-1">{r.vesselVoyage}</td>
                            <td className="border px-2 py-1">{r.vanNo}</td>
                            <td className="border px-2 py-1">{r.consignee}</td>
                            <td className="border px-2 py-1">{r.location}</td>
                            <td className="border px-2 py-1 text-right">₱{PHP(r.total)}</td>
                            {/* Merged cell: TOTAL AMOUNT PER SPAN appears once
                                per span, vertically centered across its rows. */}
                            {i === 0 && (
                              <td style={{ textAlign: 'right' }} rowSpan={span.rows.length} className="border px-2 py-1 text-center align-middle font-medium">
                                Php {PHP(span.subtotal.total)}
                              </td>
                            )}
                            <td className="border px-2 py-1">
                              <input
                                type="date"
                                defaultValue={r.dateReturn ? r.dateReturn.slice(0, 10) : ''}
                                disabled={g.isVoided || !r.lineId}
                                onBlur={(e) => handleDateReturnChange(g.billId, r.lineId, e.target.value)}
                                className="border rounded px-1 py-0.5 text-xs w-full disabled:opacity-50"
                              />
                            </td>
                          </tr>
                        ))}
                        {si < g.spans.length - 1 && (
                          <tr><td colSpan={10} className="border-0 py-1">&nbsp;</td></tr>
                        )}
                      </React.Fragment>
                    ))}

                    {/* Per-billing subtotal row */}
                    <tr className="bg-gray-100">
                      <td colSpan={8} className="border px-2 py-1 text-right font-semibold">
                        SUBTOTAL :
                      </td>
                      <td className="border px-2 py-1 text-right font-semibold">₱{PHP(g.subtotal.total)}</td>
                      <td className="border px-2 py-1"></td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>

          {summary && groups.length > 0 && (
            <p className="text-xs text-gray-500 mb-4">
              {summary.billingCount} billing{summary.billingCount === 1 ? '' : 's'} · {summary.waybillCount} waybill{summary.waybillCount === 1 ? '' : 's'} in range
            </p>
          )}

          {/* Financial Summary — grand total across all billing groups */}
          {summary && (
            <div className="flex justify-end mb-8">
              <table className="text-sm border-collapse">
                <tbody>
                  {[
                    { label: 'TOTAL:',            value: summary.grandTotal },
                    { label: 'VAT:',              value: summary.vat },
                    { label: 'Net of VaT:',       value: summary.netOfVAT },
                    { label: 'CWT:',              value: summary.cwt },
                  ].map(row => (
                    <tr key={row.label}>
                      <td className="pr-8 py-0.5 text-right text-gray-600">{row.label}</td>
                      <td className="text-right font-medium w-36">₱{PHP(row.value)}</td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-gray-800">
                    <td className="pr-8 py-1 text-right font-bold">TOTAL AMOUNT DUE:</td>
                    <td className="text-right font-bold text-gray-900 w-36">₱{PHP(summary.amountDue)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* Signature Block */}
          <div className="flex justify-between text-sm mt-12">
            <div>
              <p className="font-medium border-t border-gray-800 pt-1 pr-16">JOHN EFREN OCTAVIANO</p>
              <p className="text-gray-600 text-xs">CHIEF FINANCIAL OFFICER</p>
            </div>
            <div>
              <p>RECEIVED BY: <span className="inline-block border-b border-gray-800 w-48">&nbsp;</span></p>
              <p className="mt-4">Date: <span className="inline-block border-b border-gray-800 w-48">&nbsp;</span></p>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}
